import { Box, Stack, Typography, styled, useTheme } from "@mui/material";
import { Fragment, JSX } from "react/jsx-runtime";
import { OS_TYPES, getOSType } from "../../lib/Util";
import { KbdShortcut } from "../ui/Kbd";

const IS_MAC = getOSType() === OS_TYPES.Mac;

const differentKeys = IS_MAC
  ? { ctrl: <kbd>&#x2318;</kbd>, enter: <kbd>return</kbd>, alt: <kbd>&#x2325;</kbd> }
  : { ctrl: <kbd>ctrl</kbd>, enter: <kbd>enter</kbd>, alt: <kbd>alt</kbd> };

const commonKeys = {
  arrows: <kbd>&#x2191;/&#x2193;</kbd>,
};

const keys = { ...commonKeys, ...differentKeys };
/* To add shortcuts to this screen, you should add them to the shortcuts array, specifying the action and the shortcut keys. The keys should be specified in one of the objects defined above, depending on the OS. */

type Shortcut = {
  action: string;
  shortcut: (string | JSX.Element)[];
  windowsVariant?: (string | JSX.Element)[];
};

const SHORTCUTS_LEFT: Shortcut[] = [
  { action: "Save", shortcut: [keys.ctrl, "S"] },
  { action: "Test", shortcut: [keys.ctrl, "Y"] },
  { action: "Publish", shortcut: [keys.ctrl, keys.enter] },
  { action: "Find in file", shortcut: [keys.ctrl, "F"] },
  { action: "Go to line", shortcut: ["ctrl", "G"] },
  { action: "Select Line", shortcut: [keys.ctrl, "L"] },
];

const SHORTCUTS_RIGHT: Shortcut[] = [
  { action: "Multicursor", shortcut: [keys.alt, "click"] },
  {
    action: "Multicursor Up/Down",
    shortcut: [keys.ctrl, keys.alt, "shift", keys.arrows],
    windowsVariant: [keys.ctrl, "shift", keys.arrows],
  },
  {
    action: "Multicursor Using Keyword",
    shortcut: [keys.ctrl, "D"],
  },
  {
    action: "Move Line Up/Down",
    shortcut: [keys.alt, keys.arrows],
  },
  { action: "Delete Selected Lines ", shortcut: [keys.ctrl, "shift", "K"] },
];

const KbdShortcutStyled = styled(KbdShortcut)(({ theme }) => ({
  backgroundColor: theme.palette.surfaceLight40.main,
  color: theme.palette.text40.main,
  padding: 2,
  borderRadius: 2,
}));

const ShortcutContainer = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "10px",
});

const ShortcutColumnContainer = styled("div")({
  display: "flex",
  gap: "120px",
});

const joinKeys = (shortcutKeys: (string | JSX.Element)[]) => {
  return shortcutKeys.map((shortcutKey, index) => (
    <Fragment key={index}>
      <KbdShortcutStyled>{shortcutKey}</KbdShortcutStyled>
      {index < shortcutKeys.length - 1 && " + "}
    </Fragment>
  ));
};

const ShortcutColumn = ({ shortcuts }: { shortcuts: Shortcut[] }) => {
  const theme = useTheme();
  return (
    <ShortcutContainer>
      {shortcuts.map(({ action, shortcut, windowsVariant }) => (
        <Fragment key={action}>
          <Typography variant="body.medium.regular" color={theme.palette.text50.main}>
            {action}
          </Typography>
          <Typography variant="body.small.regular" color={theme.palette.text50.main}>
            {joinKeys(windowsVariant && !IS_MAC ? windowsVariant : shortcut)}
          </Typography>
        </Fragment>
      ))}
    </ShortcutContainer>
  );
};

const EditorEmptyState = () => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}>
      <Box
        sx={{
          bgcolor: theme.palette.surfaceLight10.main,
          flexDirection: "column",
          height: "90%",
          width: "90%",
          paddingBottom: "100px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: 8,
        }}
      >
        <Stack>
          <Typography variant="body.large.regular" sx={{ fontWeight: 600, mb: 4, color: "text50.main" }}>
            Available Shortcuts
          </Typography>
          <ShortcutColumnContainer>
            <ShortcutColumn shortcuts={SHORTCUTS_LEFT} />
            <ShortcutColumn shortcuts={SHORTCUTS_RIGHT} />
          </ShortcutColumnContainer>
        </Stack>
      </Box>
    </Box>
  );
};

export { EditorEmptyState };
